import { useEffect, useMemo, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import dropDownIcon from 'assets/dropDownIcon.svg';
import figmaFileIcon from 'assets/figma_file_icon.svg';
import noReviews from 'assets/noReviews.png';
import noReviewsFound from 'assets/noReviewsFound.png';
import ReviewsMagnifyIcon from 'assets/ReviewsMagnifyIcon.svg';
import ReviewsSearchTimer from 'assets/ReviewsSearchTimer.svg';
import ReviewsUploadIcon from 'assets/ReviewsUploadIcon.svg';

import { getDesignReviewCountsAction, getDesignReviewHistoryAction } from 'Redux/Actions/designReviewActions';

import { DESIGN_AUDIT_TYPES, DESIGN_REVIEW_STATUS, FLOW_TYPES, PLATFORM } from 'utlis/constants';
import { formatDate } from 'utlis/string_format';

import { SCORE_FILTERS } from './Overview';
import StatusBadge from './StatusBadge';

const getNpsBorderColor = score => {
  if (score > 40) {
    return '#3EDA6C';
  }
  if (score >= 0) {
    return '#FFB900';
  }

  return '#F75A5F';
};

const getNpsTextColor = score => {
  if (score > 40) {
    return '#1A4A28';
  }
  if (score >= 0) {
    return '#7B3306';
  }

  return '#72181B';
};

// Time filter constants
const TIME_FILTERS = {
  THIS_MONTH: 'This month',
  PAST_24_HOURS: 'Past 24 hours',
  LAST_7_DAYS: 'Last 7 days',
  LAST_14_DAYS: 'Last 14 days',
  LAST_30_DAYS: 'Last 30 days',
  ALL_TIME: 'All Time',
};
const FLOWS_PAGE_SIZE = 20;

const ReviewsSection = ({
  isCreateReviewOpen,
  statusFilter,
  onClearStatusFilter,
  onViewReview,
  onCreateReviewToggle,
}) => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilter, setTimeFilter] = useState(TIME_FILTERS.THIS_MONTH);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const pollingIntervalRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const designReviewCounts = useSelector(state => state.designReview.designReviewCounts);
  const designReviewHistory = useSelector(state => state.designReview.designReviewHistory);
  const reviewCount = useSelector(state => state.designReview.reviewCount);
  const tokenRefreshTimestamp = useSelector(state => state.userStore.tokenRefreshTimestamp);

  // Map time filter to days
  const getDaysFromFilter = filter => {
    switch (filter) {
      case TIME_FILTERS.THIS_MONTH:
        return new Date().getDate();
      case TIME_FILTERS.PAST_24_HOURS:
        return 1;
      case TIME_FILTERS.LAST_7_DAYS:
        return 7;
      case TIME_FILTERS.LAST_14_DAYS:
        return 14;
      case TIME_FILTERS.LAST_30_DAYS:
        return 30;
      case TIME_FILTERS.ALL_TIME:
        return 365;
      default:
        return 30;
    }
  };

  // Check if a review needs polling (processing and created within last hour)
  const needsPolling = review => {
    // Processing reviews within last hour
    if (
      review.review_status === DESIGN_REVIEW_STATUS.FETCHING_FRAME
      || review.review_status === DESIGN_REVIEW_STATUS.ANALYSING_SECTIONS
      || review.review_status === DESIGN_REVIEW_STATUS.ANALYSING_DESIGN
      || review.review_status === DESIGN_REVIEW_STATUS.SUMMARISING_FEEDBACK
    ) {
      const createdAt = new Date(review.created_at);
      const now = new Date();
      const hourInMs = 60 * 60 * 1000;

      return (now - createdAt) < hourInMs;
    }

    return false;
  };

  // Check if any reviews need polling
  const hasReviewsNeedingPolling = useMemo(
    () => designReviewHistory?.results?.some(review => needsPolling(review)) || false,
    [designReviewHistory?.results]
  );

  // Map score filter to API parameter
  const getScoreFilterForAPI = () => {
    if (statusFilter === null) {
      return null;
    }

    return statusFilter;
  };

  // Fetch design review history
  const fetchDesignReviewHistory = async (
    page = 1,
    days = 30,
    updateLoading = true,
    search = null,
    scoreFilter = null
  ) => {
    if (updateLoading) {
      setIsLoading(true);
    }

    try {
      await dispatch(getDesignReviewHistoryAction(days, page, FLOWS_PAGE_SIZE, PLATFORM, search, scoreFilter));
    }
    finally {
      // Ensure loading state is reset after fetching
      if (updateLoading) {
        setIsLoading(false);
      }
    }
  };

  // Handle search with debouncing
  const handleSearchChange = e => {
    const newSearchTerm = e.target.value;

    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for API call
    searchTimeoutRef.current = setTimeout(() => {
      const days = getDaysFromFilter(timeFilter);

      fetchDesignReviewHistory(1, days, true, newSearchTerm, getScoreFilterForAPI());
    }, 500); // 500ms debounce
  };

  useEffect(() => {
    const days = getDaysFromFilter(timeFilter);

    fetchDesignReviewHistory(currentPage, days, true, searchTerm, getScoreFilterForAPI());
  }, [dispatch, currentPage, timeFilter, statusFilter]);

  // Effect to handle status filter changes
  useEffect(() => {
    if (statusFilter !== null) {
      setCurrentPage(1); // Reset to first page when filtering
      const days = getDaysFromFilter(timeFilter);

      fetchDesignReviewHistory(1, days, true, searchTerm, getScoreFilterForAPI());
    }
  }, [statusFilter]);

  // Polling effect for processing reviews
  useEffect(() => {
    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(() => {
        // Skip API call if create review popup is open
        if (!isCreateReviewOpen) {
          const days = getDaysFromFilter(timeFilter);

          fetchDesignReviewHistory(currentPage, days, false, searchTerm, getScoreFilterForAPI());

          dispatch(getDesignReviewCountsAction(PLATFORM));
        }
      }, 10000); // Poll every 10 seconds
    };

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    if (hasReviewsNeedingPolling) {
      startPolling();
    }
    else {
      stopPolling();
    }

    return stopPolling;
  }, [hasReviewsNeedingPolling, currentPage, timeFilter, isCreateReviewOpen, searchTerm, statusFilter]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Listen for token refresh events and refetch design review history
  useEffect(() => {
    if (tokenRefreshTimestamp) {
      const days = getDaysFromFilter(timeFilter);

      fetchDesignReviewHistory(currentPage, days);
    }
  }, [tokenRefreshTimestamp, currentPage, timeFilter]);

  // Handle time filter change
  const handleTimeFilterChange = newFilter => {
    setTimeFilter(newFilter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle page change
  const handlePageChange = page => {
    setCurrentPage(page);
  };

  // Get status filter display name
  const getStatusFilterDisplay = () => {
    switch (statusFilter) {
      case SCORE_FILTERS.LOW:
        return 'Low scores';
      case SCORE_FILTERS.GOOD:
        return 'Good scores';
      case SCORE_FILTERS.GREAT:
        return 'Great scores';
      default:
        return null;
    }
  };

  const noAuditsFoundMessage = useMemo(() => {
    const scoreFilterMap = {
      [SCORE_FILTERS.LOW]: 'low score',
      [SCORE_FILTERS.GOOD]: 'good score',
      [SCORE_FILTERS.GREAT]: 'great score',
    };

    const timeFilterMap = {
      [TIME_FILTERS.THIS_MONTH]: 'this month',
      [TIME_FILTERS.PAST_24_HOURS]: 'in the past 24 hours',
      [TIME_FILTERS.LAST_7_DAYS]: 'in the last 7 days',
      [TIME_FILTERS.LAST_14_DAYS]: 'in the last 14 days',
      [TIME_FILTERS.LAST_30_DAYS]: 'in the last 30 days',
      [TIME_FILTERS.ALL_TIME]: 'to date',
    };

    const selectedScore = scoreFilterMap[statusFilter];
    const selectedTime = timeFilterMap[timeFilter] || timeFilter.toLowerCase();

    if (selectedScore) {
      return `There are no ${selectedScore} audits ${selectedTime}.`;
    }

    return `There are no audits ${selectedTime}.`;
  }, [statusFilter, timeFilter]);

  // Calculate pagination info
  const totalPages = Math.ceil(designReviewHistory.count / FLOWS_PAGE_SIZE);
  const startItem = (currentPage - 1) * FLOWS_PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * FLOWS_PAGE_SIZE, designReviewHistory.count);

  const hasNoReviews = useMemo(() => {
    if (typeof reviewCount?.reviews_done === 'number') {
      return reviewCount.reviews_done === 0;
    }

    return (designReviewCounts?.total_screens_analyzed ?? 0) === 0;
  }, [reviewCount, designReviewCounts]);

  // Generate page numbers for pagination
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    }
    else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }
    else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push('...');

      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    }
    else {
      pages.push(1);
      pages.push('...');

      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages]);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-12">
          <h2 className="font-inter text-lg font-semibold text-figma-gray-900 leading-6">
            Product Flows
          </h2>
          {statusFilter && (
            <div
              className="bg-gray-200 flex items-center px-3 py-1 gap-4 text-primary-700 rounded-full text-sm"
            >
              <span className="text-primary-700 rounded-full text-xs">
                {getStatusFilterDisplay()}
              </span>
              <button
                onClick={onClearStatusFilter}
                className="rounded-full"
                title="Clear filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm font-inter
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-140"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <img src={ReviewsMagnifyIcon} width={20} height={20} alt="Search" />
            </div>
          </div>

          {/* Time Filter */}
          <div className="flex items-center gap-8 bg-white border border-gray-300 rounded-md px-3 py-2 w-44">
            <img src={ReviewsSearchTimer} alt="Time Filter" width={20} height={20} />
            <select
              value={timeFilter}
              onChange={e => handleTimeFilterChange(e.target.value)}
              className="appearance-none bg-transparent text-sm font-inter text-gray-700 focus:outline-none"
            >
              <option>
                {TIME_FILTERS.THIS_MONTH}
              </option>
              <option>
                {TIME_FILTERS.PAST_24_HOURS}
              </option>
              <option>
                {TIME_FILTERS.LAST_7_DAYS}
              </option>
              <option>
                {TIME_FILTERS.LAST_14_DAYS}
              </option>
              <option>
                {TIME_FILTERS.LAST_30_DAYS}
              </option>
              <option>
                {TIME_FILTERS.ALL_TIME}
              </option>
            </select>
            <img src={dropDownIcon} alt="Dropdown" className="ml-auto" />
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col overflow-auto">
        {isLoading
          ? (
            <div className="bg-white rounded-lg overflow-hidden flex-1 flex justify-center items-center">
              <div className="flex justify-center items-center flex-1">
                <ImpulseSpinner frontColor={'#28BDAE'} />
              </div>
            </div>
          )
          : (
            !designReviewHistory.results?.length || designReviewHistory.results?.length === 0
              ? (
                <div className="flex flex-1 flex-col justify-center items-center py-16 overflow-auto gap-40 2xl:gap-40">
                  <img src={hasNoReviews ? noReviews : noReviewsFound} className="w-36	2xl:w-52 h-auto" />
                  <div className="flex flex-col items-center gap-16 2xl:gap-16 text-center">
                    <span className="font-inter text-lg text-gray-900 font-semibold">
                      {hasNoReviews ? 'You don\'t have any audits yet' : 'No audits found'}
                    </span>
                    <span className="font-md text-gray-900">
                      {hasNoReviews
                        ? 'Create your first audit now'
                        : noAuditsFoundMessage}
                    </span>
                  </div>
                  {hasNoReviews && (
                    <button
                      onClick={handleNewReview}
                      className="flex items-center justify-center px-6 py-3.5 bg-primary-500 text-white
                      font-inter text-base leading-6 font-medium rounded-lg shadow-shadow-xs
                      hover:bg-primary-600 transition-colors"
                    >
                      New audit
                    </button>
                  )}
                </div>
              )
              : (
                <div className="bg-white rounded-lg flex flex-col flex-1 overflow-auto">
                  {/* Table Header */}
                  <div className="flex items-center gap-16 px-16 py-12 border-b border-gray-200">
                    <div className="flex items-center">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5 overflow-hidden">
                        NPS
                      </span>
                    </div>
                    <div className="flex-[4] flex items-center">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5 overflow-hidden">
                        Flow
                      </span>
                    </div>
                    <div className="flex-[2] flex">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                        Status
                      </span>
                    </div>
                    <div className="flex-[2] flex items-center">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                        Screen location
                      </span>
                    </div>
                    <div className="flex-[2] flex items-center">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                        Last updated
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col flex-1 overflow-auto">
                    {
                      designReviewHistory?.results?.map(review => {
                        const hasFailed = review.review_status === DESIGN_REVIEW_STATUS.FAILURE;
                        const frameName = review.extra_data?.review_frame_name || 'Review Frame';
                        const isMultiFlow = review.extra_data?.flow_type === FLOW_TYPES.MULTI;
                        const additionalFramesCount = review.extra_data?.review_frame_names
                          ? review.extra_data.review_frame_names.length - 1
                          : 0;
                        const designType = review.extra_data?.design_type || DESIGN_AUDIT_TYPES.FIGMA_FRAME;
                        const isScreenshot = designType == DESIGN_AUDIT_TYPES.SCREENSHOT;

                        const rawScore = review.extra_data?.post_processes?.overview?.data?.score_assessment?.score;
                        let scoreDisplay = rawScore;

                        if (rawScore != null) {
                          if (typeof rawScore === 'string' && rawScore.trim() !== '') {
                            scoreDisplay = rawScore.trim().replace(/^\+/, '');
                          }
                        }
                        const scoreNumeric = scoreDisplay != null ? Number(scoreDisplay) : NaN;
                        // Normalize: replace Unicode minus sign (U+2212 −) with hyphen (U+002D -)
                        const score = isFinite(scoreNumeric)
                          ? String(scoreDisplay).replace(/^[-\u2212]/, '-')
                          : null;
                        const isCompleted = review.review_status === DESIGN_REVIEW_STATUS.DONE;
                        const haveScore = isCompleted && score != null;

                        return (
                          <div
                            role="button"
                            key={review.uuid}
                            className={cn('group flex items-center gap-16 px-16 py-12 border-b border-gray-10', {
                              'cursor-not-allowed opacity-60': hasFailed,
                              'hover:bg-gray-50 cursor-pointer': !hasFailed,
                            })}
                            onClick={() => !hasFailed && onViewReview(review)}
                          >
                            <div className="flex items-center">
                              <div
                                className={cn(
                                  'flex flex-row justify-center items-center w-[24px] h-[24px] rounded-full border',
                                  haveScore
                                    ? 'bg-white flex-shrink-0'
                                    : 'bg-[#F8F9FB] border-[#E1E4E8]'
                                )}
                                style={haveScore ? { borderColor: getNpsBorderColor(scoreNumeric) } : undefined}
                              >
                                <span
                                  className={cn(
                                    'font-inter text-[11px] leading-4 tracking-tight',
                                    haveScore ? 'whitespace-nowrap' : 'text-[#606776]'
                                  )}
                                  style={haveScore ? { color: getNpsTextColor(scoreNumeric) } : undefined}
                                >
                                  {haveScore ? score : '-'}
                                </span>
                              </div>
                            </div>
                            <div className="flex-[4] flex items-center">
                              <div
                                className="font-inter text-sm font-medium text-figma-gray-900 leading-5 overflow-hidden"
                              >
                                {frameName}
                                {isMultiFlow && additionalFramesCount > 0 && (
                                  <span className="text-figma-gray-500">
                                    {' '}
                                    +
                                    {' '}
                                    {additionalFramesCount}
                                    {' '}
                                    more
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex-[2] flex">
                              <StatusBadge status={review.review_status} />
                            </div>
                            <div className="flex-[2] flex items-center">
                              <div className="flex items-center gap-8">
                                <img
                                  src={isScreenshot ? ReviewsUploadIcon : figmaFileIcon}
                                  alt={isScreenshot ? 'Upload' : 'Figma'}
                                  className="w-4 h-4"
                                />
                                <span className="font-inter text-sm font-medium text-figma-gray-900 leading-5">
                                  {isScreenshot ? 'Uploaded' : (review.extra_data?.file_name || 'Figma Frame')}
                                </span>
                              </div>
                            </div>
                            <div className="flex-[2] flex items-center">
                              <span className="font-inter text-sm font-medium text-figma-gray-600 leading-5">
                                {formatDate(review.updated_at)}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>

                  {/* Pagination */}
                  {designReviewHistory.count > 0 && (
                    <div className="flex items-center justify-between px-16 py-12 border-t border-gray-200 mt-auto">
                      <div className="font-inter text-sm text-figma-gray-600">
                        Showing
                        {' '}
                        <span className="font-semibold">
                          {startItem}
                          -
                          {endItem}
                        </span>
                        {' '}
                        of
                        {' '}
                        <span className="font-semibold">
                          {designReviewHistory.count}
                        </span>
                      </div>
                      <div className="flex items-center border border-[#CBCFD6] rounded-md">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-figma-gray-600
                      hover:text-figma-gray-900 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {pageNumbers.map((page, index) => (
                          <button
                            key={index}
                            onClick={() => typeof page === 'number' && handlePageChange(page)}
                            disabled={page === '...'}
                            className={
                              `flex px-3 py-[0.375rem] flex-col justify-center items-center text-sm font-medium
                              ${page === currentPage
                            ? 'bg-primary-400 text-white'
                            : page === '...'
                              ? 'bg-white text-figma-gray-400 cursor-default'
                              : 'bg-white text-figma-gray-600 hover:text-figma-gray-900'
                          }`
                            }
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium
                      text-figma-gray-600 hover:text-figma-gray-900 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
          )}
      </div>
    </>
  );

  function handleNewReview() {
    onCreateReviewToggle(true);
  }
};

ReviewsSection.propTypes = {
  isCreateReviewOpen: PropTypes.bool.isRequired,
  statusFilter: PropTypes.string,
  onClearStatusFilter: PropTypes.func.isRequired,
  onViewReview: PropTypes.func.isRequired,
  onCreateReviewToggle: PropTypes.func.isRequired,
};

export default ReviewsSection;
